import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import confetti from "canvas-confetti";
import api from "../lib/api";
import { Trophy, PaperPlaneTilt, UsersThree, Handshake, LinkSimple, ChatText, EnvelopeSimple, ArrowLeft, CircleNotch, Confetti } from "@phosphor-icons/react";

const money = (n) => `$${(n || 0).toLocaleString()}`;
const STATUS_STYLE = {
  invited: "bg-zinc-100 text-zinc-600 border-zinc-200",
  joined: "bg-indigo-50 text-indigo-700 border-indigo-200",
  engaged: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

function Stat({ icon: Icon, label, value, accent, testId }) {
  return (
    <div className="rounded-lg border border-zinc-200 bg-white p-4" data-testid={testId}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-zinc-400 mb-1"><Icon size={13} /> {label}</div>
      <p className={`font-heading text-2xl font-medium ${accent || "text-zinc-900"}`}>{value}</p>
    </div>
  );
}

function MilestoneBar({ engaged, milestones }) {
  const max = milestones[milestones.length - 1];
  const pct = Math.min((engaged / max) * 100, 100);
  return (
    <div className="rounded-lg border border-zinc-200 bg-white p-5" data-testid="milestone-bar">
      <div className="flex items-center justify-between mb-1">
        <h3 className="text-sm font-medium text-zinc-900 flex items-center gap-1.5"><Trophy size={15} className="text-amber-500" /> Milestone tracker</h3>
        <span className="text-[11px] text-zinc-500">{engaged} friend{engaged === 1 ? "" : "s"} engaged</span>
      </div>
      <p className="text-[11px] text-zinc-500 mb-4">Every friend who becomes a client earns you <span className="font-medium text-emerald-700">$50</span> in credit.</p>
      <div className="relative h-2 rounded-full bg-zinc-100">
        <div className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-emerald-400 to-emerald-600 transition-all duration-700" style={{ width: `${pct}%` }} />
        {milestones.map((m) => (
          <div key={m} className="absolute -top-1" style={{ left: `calc(${(m / max) * 100}% - 8px)` }}>
            <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center text-[8px] font-bold ${engaged >= m ? "bg-emerald-500 border-emerald-500 text-white" : "bg-white border-zinc-300 text-zinc-400"}`}>{m}</div>
            <p className={`text-[9px] mt-1 -ml-1 font-mono ${engaged >= m ? "text-emerald-700" : "text-zinc-400"}`}>${m * 50}</p>
          </div>
        ))}
      </div>
      <p className="text-[11px] text-zinc-600 mt-8" data-testid="milestone-message">
        {engaged >= max
          ? "You've hit every milestone — you're a legend. Keep sharing!"
          : `${milestones.find((m) => engaged < m) - engaged} more engaged friend${milestones.find((m) => engaged < m) - engaged === 1 ? "" : "s"} to reach your next milestone (${money(milestones.find((m) => engaged < m) * 50)} total earned).`}
      </p>
    </div>
  );
}

export default function RewardsPage() {
  const { data, isLoading } = useQuery({ queryKey: ["rewards"], queryFn: () => api.get("/referrals/rewards").then((r) => r.data) });
  const [celebrate, setCelebrate] = useState(null);

  useEffect(() => {
    if (!data?.code) return;
    const hit = [...(data.milestones || [])].reverse().find((m) => (data.friends_engaged || 0) >= m);
    if (!hit) return;
    const key = `rewards_celebrated_${data.code}_${hit}`;
    if (localStorage.getItem(key)) return;
    localStorage.setItem(key, "1");
    setCelebrate(hit);
    confetti({ particleCount: 160, spread: 75, origin: { y: 0.6 } });
    setTimeout(() => confetti({ particleCount: 90, angle: 60, spread: 60, origin: { x: 0, y: 0.7 } }), 350);
    setTimeout(() => confetti({ particleCount: 90, angle: 120, spread: 60, origin: { x: 1, y: 0.7 } }), 600);
  }, [data]);

  if (isLoading) return <div className="min-h-screen bg-zinc-50 flex items-center justify-center text-sm text-zinc-400"><CircleNotch size={18} className="animate-spin mr-2" /> Loading your rewards…</div>;

  const url = `${window.location.origin}/refer/${data?.code}`;
  const msg = encodeURIComponent(`My tax advisory firm has been fantastic — secure portal, snap documents from your phone, never miss a deadline. Check them out: ${url}`);
  const copy = async () => {
    try { await navigator.clipboard.writeText(url); } catch {
      const ta = document.createElement("textarea"); ta.value = url; document.body.appendChild(ta); ta.select(); document.execCommand("copy"); ta.remove();
    }
    toast.success("Invite link copied — share it anywhere");
  };

  const funnel = [
    { icon: PaperPlaneTilt, label: "Invites sent", value: data?.invites_sent || 0 },
    { icon: UsersThree, label: "Friends joined", value: data?.friends_joined || 0 },
    { icon: Handshake, label: "Became clients", value: data?.friends_engaged || 0 },
  ];

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="rewards-page">
      <header className="bg-white border-b border-zinc-200 px-4 md:px-8 py-4 flex items-center justify-between sticky top-0 z-10">
        <Link data-testid="rewards-back-link" to="/portal" className="flex items-center gap-1.5 text-xs text-zinc-500 hover:text-zinc-900 transition-colors duration-150"><ArrowLeft size={14} /> Back to portal</Link>
        <p className="font-heading font-bold text-sm text-zinc-900 flex items-center gap-1.5"><Confetti size={16} className="text-amber-500" /> Referral Rewards</p>
        <span className="w-20" />
      </header>

      <div className="max-w-3xl mx-auto p-4 md:p-8 space-y-5 fade-up">
        {celebrate && (
          <div data-testid="milestone-celebration" className="rounded-lg border border-amber-300 bg-gradient-to-r from-amber-50 to-emerald-50 p-4 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <Confetti size={28} weight="duotone" className="text-amber-500 shrink-0" />
              <div>
                <p className="text-sm font-medium text-zinc-900">Milestone reached — {celebrate} friend{celebrate === 1 ? "" : "s"} became client{celebrate === 1 ? "" : "s"}! 🎉</p>
                <p className="text-[11px] text-zinc-600">That's {money(celebrate * 50)} earned in referral credit. Keep sharing to hit the next one.</p>
              </div>
            </div>
            <button data-testid="celebration-dismiss-btn" onClick={() => setCelebrate(null)} className="text-[11px] text-zinc-500 underline hover:no-underline shrink-0">Dismiss</button>
          </div>
        )}
        <div className="rounded-lg bg-zinc-900 text-white p-6 flex items-center justify-between" data-testid="rewards-hero">
          <div>
            <p className="text-[10px] uppercase tracking-wide text-zinc-400 mb-1">Credit available now</p>
            <p className="font-heading text-4xl font-medium text-emerald-400">{money(data?.credit_available)}</p>
            <p className="text-[11px] text-zinc-400 mt-1">Lifetime earned {money(data?.credit_earned)} · Redeemed {money(data?.credit_redeemed)}</p>
          </div>
          <Trophy size={52} weight="duotone" className="text-amber-400" />
        </div>

        <div className="grid grid-cols-3 gap-3">
          {funnel.map((f, i) => <Stat key={f.label} icon={f.icon} label={f.label} value={f.value} testId={`funnel-stat-${i}`} accent={i === 2 ? "text-emerald-700" : undefined} />)}
        </div>

        <MilestoneBar engaged={data?.friends_engaged || 0} milestones={data?.milestones || [1, 3, 5, 10]} />

        <div className="rounded-lg border border-dashed border-zinc-300 bg-white p-5" data-testid="rewards-share-block">
          <h3 className="text-sm font-medium text-zinc-900 mb-1">Share your personal invite link</h3>
          <p className="text-[11px] font-mono text-zinc-600 truncate mb-3" data-testid="rewards-invite-url">{url}</p>
          <div className="flex flex-wrap gap-2">
            <button data-testid="rewards-copy-btn" onClick={copy} className="flex items-center gap-1.5 text-xs text-zinc-700 border border-zinc-200 rounded-md px-3 py-1.5 hover:bg-zinc-50 transition-colors duration-150"><LinkSimple size={13} /> Copy link</button>
            <a data-testid="rewards-sms-btn" href={`sms:?&body=${msg}`} className="flex items-center gap-1.5 text-xs text-zinc-700 border border-zinc-200 rounded-md px-3 py-1.5 hover:bg-zinc-50 transition-colors duration-150"><ChatText size={13} /> Text it</a>
            <a data-testid="rewards-email-btn" href={`mailto:?subject=${encodeURIComponent("A tax advisory firm worth knowing")}&body=${msg}`} className="flex items-center gap-1.5 text-xs text-zinc-700 border border-zinc-200 rounded-md px-3 py-1.5 hover:bg-zinc-50 transition-colors duration-150"><EnvelopeSimple size={13} /> Email it</a>
          </div>
        </div>

        <div className="rounded-lg border border-zinc-200 bg-white p-5" data-testid="rewards-activity">
          <h3 className="text-sm font-medium text-zinc-900 mb-3">Recent activity</h3>
          {(data?.activity || []).map((a, i) => (
            <div key={i} className="flex items-center justify-between py-1.5 border-b border-zinc-100 last:border-0">
              <div>
                <p className="text-xs text-zinc-800">{a.name}</p>
                <p className="text-[10px] text-zinc-400">{new Date(a.created_at).toLocaleDateString()}</p>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-[9px] uppercase border rounded-sm px-1.5 py-0.5 ${STATUS_STYLE[a.status] || STATUS_STYLE.invited}`}>{a.status}</span>
                <span className={`font-mono text-[11px] ${a.status === "engaged" ? "text-emerald-700" : "text-zinc-400"}`}>${a.bonus_amount}</span>
              </div>
            </div>
          ))}
          {!data?.activity?.length && <p className="text-xs text-zinc-400 italic">No invites yet — share your link above to start earning.</p>}
        </div>
      </div>
    </div>
  );
}
